import React, { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { observer } from "mobx-react";
import { AntDesign, Ionicons } from "@expo/vector-icons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Colors from "../../../../Resources/Color";
import Fonts from "../../../../Resources/Fonts";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import Tooltip from "../../../Components/Tooltip";
import { FoodPickerPanel } from "../../shared/foodPicker/FoodPickerPanel";
import { getEditorFoodSourceLabel } from "../../shared/editorFoodSourceLabel";
import { SelectedFoodRow } from "../../shared/foodRows/SelectedFoodRow";
import { getSelectedRowDeleteTooltipKey } from "../../shared/foodRows/selectedRowDeleteTooltip";
import {
  getFoodStableKey,
  getIngredientSourceLabel,
} from "../../recipes/recipeLogic";
import { getLocalizedFoodName } from "../../foods/foodCatalog";

const SortableEquivalentOptionCard = observer(function SortableEquivalentOptionCard({
  t,
  option,
  isLast,
  useStackedLayout,
  getOptionTotals,
  updateSelectedOptionQuantity,
  changeSelectedOptionUnit,
  removeOption,
  onSelectOption,
  isSelected,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(
      transform
        ? {
            ...transform,
            scaleX: 1,
            scaleY: 1,
          }
        : null,
    ),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 0,
    position: "relative",
  };

  return (
    <View ref={setNodeRef} style={style}>
      <SelectedFoodRow
        title={option.name}
        subtitle={getEditorFoodSourceLabel(option, t)}
        quantityValue={String(option.quantity ?? "")}
        onChangeQuantity={(value) => updateSelectedOptionQuantity(option.id, value)}
        unitOptions={[]}
        selectedUnitKey="grams"
        onSelectUnit={undefined}
        referenceText=""
        totals={option}
        compactLayout={useStackedLayout}
        isLast={isLast}
        chromeVariant="transparent"
        showQuantityControl={false}
        showNutritionSummary={true}
        nutritionSummaryLabel={t("per100gShort-string")}
        dragHandleProps={{
          attributes,
          listeners,
          isDragging,
        }}
        leadingSlot={
          <View style={styles.optionIconWrap}>
            <Ionicons
              name="git-compare-outline"
              size={14}
              color={Colors.bubbleColor}
            />
          </View>
        }
        actionSlot={
          <View style={styles.rowActionGroup}>
            <HoverableTouchableOpacity
              activeOpacity={0.85}
              onPress={() => onSelectOption(option.id)}
              style={[
                styles.overviewButton,
                isSelected ? styles.overviewButtonActive : null,
              ]}
              hoverStyle={!isSelected ? styles.overviewButtonHover : null}
            >
              <Ionicons
                name="analytics-outline"
                size={16}
                color={isSelected ? Colors.bubbleColor : Colors.darkgrayColor}
              />
            </HoverableTouchableOpacity>
            <Tooltip
              content={getSelectedRowDeleteTooltipKey({
                entityKind: "equivalent",
              })}
              placement="top"
            >
              <View>
                <HoverableTouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => removeOption(option.id)}
                  style={styles.removeOptionButton}
                  hoverStyle={styles.removeOptionButtonHover}
                >
                  <Ionicons
                    name="trash-outline"
                    size={16}
                    color={Colors.darkgrayColor}
                  />
                </HoverableTouchableOpacity>
              </View>
            </Tooltip>
          </View>
        }
      />
    </View>
  );
});

export const EquivalentOptionsSection = observer(function EquivalentOptionsSection({
  t,
  form,
  saveValidationMessage,
  sensors,
  handleDragEnd,
  useStackedLayout,
  getOptionTotals,
  updateSelectedOptionQuantity,
  changeSelectedOptionUnit,
  removeOption,
  searchInputRef,
  optionSearchInputPressingRef,
  focusOptionSearchInput,
  optionSearchText,
  setOptionSearchText,
  optionSortOptions,
  selectedSortOption,
  optionSortKey,
  setOptionSortKey,
  databaseOptions,
  selectedDatabaseOption,
  optionDatabaseKey,
  setOptionDatabaseKey,
  optionResults,
  optionFoodIsLoading,
  optionFoodLoadingSessionId,
  language,
  addOption,
  onCreateFood,
  isFoodPickerOpen,
  openFoodPicker,
  closeFoodPicker,
  selectedOptionId,
  setSelectedOptionId,
  pickerPanelRootRef,
}) {
  const pickerPanelProps = useMemo(
    () => ({
      t,
      searchInputRef,
      inputPressingRef: optionSearchInputPressingRef,
      focusSearchInput: focusOptionSearchInput,
      searchText: optionSearchText,
      setSearchText: setOptionSearchText,
      sortOptions: optionSortOptions,
      selectedSortOption,
      sortKey: optionSortKey,
      setSortKey: setOptionSortKey,
      databaseOptions,
      selectedDatabaseOption,
      databaseKey: optionDatabaseKey,
      setDatabaseKey: setOptionDatabaseKey,
      results: optionResults,
      isLoading: optionFoodIsLoading,
      loadingSessionId: optionFoodLoadingSessionId,
      showRowQuantityControl: false,
      showRowNutritionSummary: true,
      rowNutritionSummaryLabel: t("per100gShort-string"),
      getRowKey: (food, index) =>
        String(food?.id || food?.code || `${getFoodStableKey(food, language)}-${index}`),
      getRowTitle: (food) => getLocalizedFoodName(food, language),
      getRowSubtitle: (food) => getIngredientSourceLabel(food, t),
      getRowReferenceText: () => "",
      getRowTotals: (food) => food,
      canSelectRow: () => true,
      onSelectRow: (food) => addOption(food),
      onCreateFood,
      title: t("addFoodEquivalent-string"),
      emptyText: t("noIngredientResults-string"),
      onClose: closeFoodPicker,
      isInline: true,
      compactLayout: useStackedLayout,
      selectSearchTextOnFocus: false,
      topMarginOverride: 8,
    }),
    [
      t,
      searchInputRef,
      optionSearchInputPressingRef,
      focusOptionSearchInput,
      optionSearchText,
      setOptionSearchText,
      optionSortOptions,
      selectedSortOption,
      optionSortKey,
      setOptionSortKey,
      databaseOptions,
      selectedDatabaseOption,
      optionDatabaseKey,
      setOptionDatabaseKey,
      optionFoodIsLoading,
      optionFoodLoadingSessionId,
      optionResults,
      language,
      addOption,
      onCreateFood,
      useStackedLayout,
    ],
  );

  return (
    <>
      <View style={styles.optionsSectionHeader}>
        <View style={styles.optionsSectionLine} />
        <Text style={styles.optionsSectionLabel}>{t("foods-string")}</Text>
        <View style={styles.optionsSectionLine} />
      </View>

      {saveValidationMessage ? (
        <View style={styles.saveNotice}>
          <Ionicons
            name="alert-circle-outline"
            size={14}
            color={Colors.warningTextColor}
          />
          <Text style={styles.saveNoticeText}>{saveValidationMessage}</Text>
        </View>
      ) : null}

      {form.options.length > 0 ? (
        <View style={styles.selectedOptionsList}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={form.options.map((option) => option.id)}
              strategy={verticalListSortingStrategy}
            >
              {form.options.map((option, index) => (
                <SortableEquivalentOptionCard
                  key={option.id}
                  t={t}
                  option={option}
                  isLast={index === form.options.length - 1}
                  useStackedLayout={useStackedLayout}
                  getOptionTotals={getOptionTotals}
                  updateSelectedOptionQuantity={updateSelectedOptionQuantity}
                  changeSelectedOptionUnit={changeSelectedOptionUnit}
                  removeOption={removeOption}
                  onSelectOption={setSelectedOptionId}
                  isSelected={selectedOptionId === option.id}
                />
              ))}
            </SortableContext>
          </DndContext>
        </View>
      ) : (
        <View style={styles.selectedOptionsEmpty}>
          <View style={styles.emptySwapBoard}>
            <View style={styles.emptySwapBoardGlow} />
            <View style={[styles.emptySwapChip, styles.emptySwapChipLeft]}>
              <View style={styles.emptySwapChipIconWrap}>
                <Ionicons
                  name="nutrition-outline"
                  size={14}
                  color={Colors.bubbleColor}
                />
              </View>
              <View style={styles.emptySwapChipTextStack}>
                <View style={styles.emptySwapChipLineShort} />
                <View style={styles.emptySwapChipLineLong} />
              </View>
            </View>
            <View style={styles.emptySwapConnectorSegment} />
            <View style={styles.emptySwapBadge}>
              <Ionicons
                name="swap-horizontal"
                size={16}
                color={Colors.bubbleColor}
              />
            </View>
            <View style={styles.emptySwapConnectorSegment} />
            <View style={[styles.emptySwapChip, styles.emptySwapChipRight]}>
              <View style={styles.emptySwapChipIconWrap}>
                <Ionicons
                  name="cafe-outline"
                  size={14}
                  color={Colors.bubbleColor}
                />
              </View>
              <View style={styles.emptySwapChipTextStack}>
                <View style={styles.emptySwapChipLineShort} />
                <View style={styles.emptySwapChipLineLong} />
              </View>
            </View>
          </View>
          <Text style={styles.selectedOptionsEmptyTitle}>
            {t("equivalentEmptySwapTitle-string")}
          </Text>
          <Text style={styles.selectedOptionsEmptyBody}>
            {t("equivalentEmptySwapBody-string")}
          </Text>
        </View>
      )}

      {!isFoodPickerOpen ? (
        <HoverableTouchableOpacity
          activeOpacity={0.85}
          onPress={openFoodPicker}
          style={styles.addFoodButton}
          hoverStyle={styles.addFoodButtonHover}
        >
          <AntDesign name="plus" size={12} color={Colors.bubbleColor} />
          <Text style={styles.addFoodButtonText}>
            {t(
              form.options.length === 0
                ? "equivalentAddFirstFood-string"
                : "addFoodEquivalent-string",
            )}
          </Text>
        </HoverableTouchableOpacity>
      ) : null}

      {isFoodPickerOpen ? (
        <FoodPickerPanel
          {...pickerPanelProps}
          panelRootRef={pickerPanelRootRef}
        />
      ) : null}
    </>
  );
});

const styles = StyleSheet.create({
  optionsSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 18,
    marginBottom: 14,
  },
  optionsSectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: Colors.borderColor,
  },
  optionsSectionLabel: {
    ...Fonts.medium12,
    color: Colors.grayColor,
    fontWeight: "400",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    textAlign: "center",
    paddingHorizontal: 4,
  },
  saveNotice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 9,
    borderRadius: 10,
    backgroundColor: Colors.warningBackgroundColor,
    borderWidth: 0.5,
    borderColor: "rgba(194, 65, 12, 0.2)",
    marginBottom: 12,
  },
  saveNoticeText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 17,
    color: Colors.darkgrayColor,
  },
  selectedOptionsList: {
    position: "relative",
    zIndex: 2,
  },
  selectedOptionsEmpty: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 44,
    gap: 8,
  },
  emptySwapBoard: {
    width: 248,
    maxWidth: "100%",
    minHeight: 112,
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "rgba(63, 123, 141, 0.12)",
    backgroundColor: "rgba(63, 123, 141, 0.035)",
    overflow: "hidden",
    marginBottom: 8,
  },
  emptySwapBoardGlow: {
    position: "absolute",
    width: 160,
    height: 160,
    borderRadius: 80,
    backgroundColor: "rgba(63, 123, 141, 0.035)",
  },
  emptySwapConnectorSegment: {
    width: 10,
    borderTopWidth: 1,
    borderStyle: "dashed",
    borderColor: "rgba(63, 123, 141, 0.22)",
    marginHorizontal: 3,
  },
  emptySwapBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "rgba(63, 123, 141, 0.16)",
    backgroundColor: "rgba(255, 255, 255, 0.96)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  emptySwapChip: {
    width: 76,
    height: 68,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "rgba(63, 123, 141, 0.16)",
    backgroundColor: "rgba(255, 255, 255, 0.92)",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  emptySwapChipLeft: {
    transform: [{ rotate: "-5deg" }],
  },
  emptySwapChipRight: {
    transform: [{ rotate: "5deg" }],
  },
  emptySwapChipIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "rgba(63, 123, 141, 0.09)",
    alignItems: "center",
    justifyContent: "center",
  },
  emptySwapChipTextStack: {
    alignItems: "center",
    gap: 5,
  },
  emptySwapChipLineShort: {
    width: 28,
    height: 4,
    borderRadius: 999,
    backgroundColor: "rgba(63, 123, 141, 0.14)",
  },
  emptySwapChipLineLong: {
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: "rgba(63, 123, 141, 0.1)",
  },
  selectedOptionsEmptyTitle: {
    ...Fonts.medium14,
    color: Colors.darkgrayColor,
    textAlign: "center",
  },
  selectedOptionsEmptyBody: {
    maxWidth: 290,
    fontSize: 13,
    lineHeight: 19,
    color: Colors.grayColor,
    textAlign: "center",
  },
  addFoodButton: {
    marginTop: 12,
    minHeight: 42,
    borderRadius: 12,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transitionProperty: "opacity",
    transitionDuration: "160ms",
    transitionTimingFunction: "ease",
  },
  addFoodButtonHover: {
    opacity: 0.82,
  },
  addFoodButtonText: {
    ...Fonts.medium13,
    color: Colors.bubbleColor,
  },
  optionIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(63, 123, 141, 0.08)",
    flexShrink: 0,
  },
  rowActionGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: 80,
    flexShrink: 0,
    justifyContent: "flex-end",
  },
  removeOptionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  removeOptionButtonHover: {
    backgroundColor: "rgba(240, 68, 56, 0.05)",
    borderColor: "rgba(240, 68, 56, 0.18)",
  },
  overviewButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  overviewButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  overviewButtonActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
});
